import LegalPageShell from "@/components/site/LegalPageShell";
import { useLanguage, pick } from "@/lib/i18n";
import { SUPPORT_EMAIL, OWNER_NAME, OWNER_ADDRESS } from "@/lib/translations";

const COPY = {
    en: {
        title: "Privacy Policy",
        subtitle:
            "How Dino Detective handles information when you visit this website or use the Dino Detective mobile app.",
        sections: [
            { id: "overview", label: "Overview" },
            { id: "collected", label: "Information we collect" },
            { id: "not-collected", label: "Information we do not collect" },
            { id: "purchase", label: "Premium purchase handling" },
            { id: "device", label: "Device information" },
            { id: "security", label: "Security" },
            { id: "parent", label: "Parent contact information" },
            { id: "retention", label: "Data retention" },
            { id: "children", label: "Children's privacy" },
            { id: "contact", label: "Contact details" },
        ],
    },
    de: {
        title: "Datenschutzerklärung",
        subtitle:
            "Wie Dino Detective mit Informationen umgeht, wenn du diese Website besuchst oder die Dino-Detective-App verwendest.",
        sections: [
            { id: "overview", label: "Überblick" },
            { id: "collected", label: "Erhobene Informationen" },
            { id: "not-collected", label: "Nicht erhobene Informationen" },
            { id: "purchase", label: "Premium-Käufe" },
            { id: "device", label: "Geräteinformationen" },
            { id: "security", label: "Sicherheit" },
            { id: "parent", label: "Eltern-Kontaktdaten" },
            { id: "retention", label: "Datenaufbewahrung" },
            { id: "children", label: "Datenschutz für Kinder" },
            { id: "contact", label: "Kontaktdaten" },
        ],
    },
};

export default function PrivacyPolicy() {
    const { lang } = useLanguage();
    const t = pick(COPY, lang);

    return (
        <LegalPageShell title={t.title} subtitle={t.subtitle} sections={t.sections}>
            {lang === "en" ? <EN /> : <DE />}
        </LegalPageShell>
    );
}

function EN() {
    return (
        <>
            <h2 id="overview">Overview</h2>
            <p>
                Dino Detective (the “App”) is a family-friendly educational mobile game owned and operated by {OWNER_NAME}, based in Germany.
                This Privacy Policy explains what information we collect, how it is used, how it is protected, and what choices you have when
                you use the App or visit <strong>dino-detective.app</strong> (the “Website”). We take a deliberately minimal-data approach: we
                collect as little as possible, and we never sell or share data for advertising.
            </p>

            <h2 id="collected">Information we collect</h2>
            <p>The App is designed to work offline and without accounts. The limited information we may process includes:</p>
            <ul>
                <li><strong>Purchase records</strong> – when you buy the one-time Premium upgrade, Google Play sends us a purchase token so we can validate and restore your purchase. We do not see your payment card or full payment details.</li>
                <li><strong>Local game progress</strong> – puzzles solved, dinosaurs unlocked, badges earned and certificate names are stored locally on your device. They are not transmitted to our servers.</li>
                <li><strong>Support correspondence</strong> – if you email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>, we keep your message and any reply for as long as needed to provide support.</li>
                <li><strong>Crash and diagnostic data</strong> – Google Play may collect technical crash reports to help us fix bugs. We use this only to improve stability.</li>
            </ul>

            <h2 id="not-collected">Information we do not collect</h2>
            <ul>
                <li>No name, address, phone number, email or date of birth of the child playing.</li>
                <li>No user accounts, profiles or sign-ins.</li>
                <li>No microphone, camera, contacts, photos or location data.</li>
                <li>No third-party advertising identifiers and no advertising SDKs.</li>
                <li>No Google Analytics, no Meta Pixel and no behavioural tracking.</li>
                <li>No selling or sharing of personal data with brokers, advertisers or partners.</li>
            </ul>

            <h2 id="purchase">Premium purchase handling</h2>
            <p>
                Dino Detective offers a one-time premium upgrade through Google Play Billing. Google Play processes the payment and the billing
                information stays with Google — we never see, store or process card details. When a purchase is completed, Google Play provides
                an anonymous purchase token to the App so we can unlock the premium content and support the “Restore purchases” feature on the
                same Google account. The premium upgrade is not a subscription and does not renew automatically.
            </p>

            <h2 id="device">Device information</h2>
            <p>
                The App uses standard Android system permissions required for purchases and basic operation (e.g. billing service). The Website
                may briefly process minimal connection information (such as IP address and browser type) at the hosting layer to deliver pages
                and protect against abuse. This information is not used for tracking or profiling.
            </p>

            <h2 id="security">Security</h2>
            <p>
                We use industry-standard measures to protect any information we do hold — including HTTPS for the Website, secure billing through
                Google Play, and limiting access to support email on a need-to-know basis. No method of transmission over the internet is 100%
                secure, but the data we handle is intentionally minimal to reduce risk.
            </p>

            <h2 id="parent">Parent contact information</h2>
            <p>
                Dino Detective is built for children and families. The App does not collect a child’s personal data and does not ask children to
                contact us directly. Parents, guardians or supervising adults may contact us at any time at{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> to:
            </p>
            <ul>
                <li>Ask any privacy-related question.</li>
                <li>Request information about data that may relate to a purchase made on a Google account.</li>
                <li>Request deletion of support correspondence.</li>
            </ul>

            <h2 id="retention">Data retention</h2>
            <ul>
                <li>Game progress: stored on the device until you uninstall the App or clear app data.</li>
                <li>Purchase records: kept only as long as needed to comply with tax, accounting and consumer-protection law.</li>
                <li>Support emails: kept until the issue is resolved, then deleted on request or within a reasonable period.</li>
            </ul>

            <h2 id="children">Children&rsquo;s privacy</h2>
            <p>
                Dino Detective is designed for children. We follow the principles of the Google Play Families Policy, the EU GDPR (including
                Article 8 on children’s consent) and, where applicable, the United States COPPA framework. We do not knowingly collect personal
                data from children. If you believe a child has provided personal data to us (for example via a support email), please contact
                us and we will delete it promptly.
            </p>

            <h2 id="contact">Contact details</h2>
            <p>
                <strong>{OWNER_NAME}</strong><br />
                {OWNER_ADDRESS}<br />
                Email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </p>
        </>
    );
}

function DE() {
    return (
        <>
            <h2 id="overview">Überblick</h2>
            <p>
                Dino Detective (die „App“) ist ein familienfreundliches Lernspiel, das von {OWNER_NAME} mit Sitz in Deutschland betrieben wird.
                Diese Datenschutzerklärung erklärt, welche Informationen wir erheben, wie sie genutzt werden, wie sie geschützt werden und welche
                Wahlmöglichkeiten du hast, wenn du die App oder die Website <strong>dino-detective.app</strong> nutzt. Wir verfolgen einen
                bewusst datensparsamen Ansatz: wir erheben so wenig wie möglich und geben keine Daten zu Werbezwecken weiter.
            </p>

            <h2 id="collected">Erhobene Informationen</h2>
            <p>Die App funktioniert offline und ohne Konto. Wir verarbeiten lediglich folgende Informationen:</p>
            <ul>
                <li><strong>Kaufdaten</strong> – beim einmaligen Premium-Kauf übermittelt Google Play uns einen Kauf-Token, damit wir den Kauf prüfen und wiederherstellen können. Kartendaten sehen wir nicht.</li>
                <li><strong>Spielfortschritt</strong> – gelöste Rätsel, freigeschaltete Dinosaurier, Abzeichen und Urkundennamen werden nur lokal auf deinem Gerät gespeichert.</li>
                <li><strong>Support-Korrespondenz</strong> – wenn du an <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> schreibst, speichern wir deine Nachricht solange wie nötig.</li>
                <li><strong>Absturz- und Diagnosedaten</strong> – Google Play kann technische Absturzberichte erfassen, um Fehler zu beheben.</li>
            </ul>

            <h2 id="not-collected">Nicht erhobene Informationen</h2>
            <ul>
                <li>Keine Namen, Adressen, Telefonnummern, E-Mails oder Geburtsdaten der spielenden Kinder.</li>
                <li>Keine Konten, Profile oder Anmeldungen.</li>
                <li>Kein Zugriff auf Mikrofon, Kamera, Kontakte, Fotos oder Standort.</li>
                <li>Keine Werbe-IDs und keine Werbe-SDKs.</li>
                <li>Kein Google Analytics, kein Meta Pixel, kein Tracking.</li>
                <li>Kein Verkauf oder Teilen personenbezogener Daten mit Werbepartnern oder Datenhändlern.</li>
            </ul>

            <h2 id="purchase">Premium-Käufe</h2>
            <p>
                Dino Detective bietet ein einmaliges Premium-Upgrade über Google Play Billing. Google Play wickelt die Zahlung ab; Kartendaten
                bleiben bei Google. Nach erfolgreichem Kauf erhält die App einen anonymen Kauf-Token, um Premium-Inhalte freizuschalten und die
                Funktion „Käufe wiederherstellen“ auf demselben Google-Konto zu ermöglichen. Es handelt sich nicht um ein Abo.
            </p>

            <h2 id="device">Geräteinformationen</h2>
            <p>
                Die App nutzt Standard-Android-Berechtigungen, die für Käufe und Grundfunktionen nötig sind. Die Website kann auf Hosting-Ebene
                kurzzeitig minimale technische Daten (z. B. IP-Adresse, Browsertyp) verarbeiten, um Seiten auszuliefern und vor Missbrauch zu
                schützen. Es findet kein Tracking statt.
            </p>

            <h2 id="security">Sicherheit</h2>
            <p>
                Wir verwenden branchenübliche Maßnahmen wie HTTPS, sichere Abwicklung über Google Play sowie einen restriktiven Zugriff auf
                Support-E-Mails. Wir verarbeiten bewusst möglichst wenige Daten, um Risiken zu reduzieren.
            </p>

            <h2 id="parent">Eltern-Kontaktdaten</h2>
            <p>
                Dino Detective ist für Kinder und Familien gemacht. Die App erhebt keine personenbezogenen Daten von Kindern und fordert Kinder
                nicht zur Kontaktaufnahme auf. Eltern oder Erziehungsberechtigte können sich jederzeit an{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> wenden, um Datenschutzfragen zu klären oder die Löschung von
                Support-Korrespondenz zu beantragen.
            </p>

            <h2 id="retention">Datenaufbewahrung</h2>
            <ul>
                <li>Spielfortschritt: nur auf deinem Gerät, bis die App deinstalliert oder die App-Daten gelöscht werden.</li>
                <li>Kaufdaten: nur so lange wie für Steuer-, Buchhaltungs- und Verbraucherschutzrecht erforderlich.</li>
                <li>Support-E-Mails: bis zur Klärung des Anliegens, danach auf Anfrage oder innerhalb angemessener Frist gelöscht.</li>
            </ul>

            <h2 id="children">Datenschutz für Kinder</h2>
            <p>
                Dino Detective ist für Kinder konzipiert. Wir richten uns nach den Vorgaben der Google Play Families Policy, der DSGVO
                (insbesondere Art. 8) und – wo anwendbar – nach den Grundsätzen von COPPA. Wir erheben wissentlich keine personenbezogenen Daten
                von Kindern. Sollten dennoch Daten eines Kindes an uns gelangen, löschen wir sie unverzüglich nach Hinweis.
            </p>

            <h2 id="contact">Kontaktdaten</h2>
            <p>
                <strong>{OWNER_NAME}</strong><br />
                {OWNER_ADDRESS}<br />
                E-Mail: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </p>
        </>
    );
}
